import { z } from 'zod';

export const opportunityStages = [
  'new_inquiry',
  'brief_in_progress',
  'technical_review',
  'costing',
  'regulatory_review',
  'internal_approval',
  'ready_to_send',
  'sent',
  'changes_requested',
  'accepted',
  'deposit_pending',
  'handoff_ready',
  'handed_off',
  'closed_won',
  'closed_lost',
  'expired',
] as const;

export const quotationStates = [
  'draft',
  'costing_review',
  'regulatory_review',
  'management_approval',
  'approved_to_send',
  'sent',
  'changes_requested',
  'accepted',
  'expired',
  'withdrawn',
  'superseded',
] as const;

export type OpportunityStage = (typeof opportunityStages)[number];
export type QuotationState = (typeof quotationStates)[number];

const opportunityTransitions: Readonly<Record<OpportunityStage, readonly OpportunityStage[]>> = {
  new_inquiry: ['brief_in_progress', 'closed_lost'],
  brief_in_progress: ['technical_review', 'closed_lost'],
  technical_review: ['brief_in_progress', 'costing', 'closed_lost'],
  costing: ['technical_review', 'regulatory_review', 'closed_lost'],
  regulatory_review: ['costing', 'internal_approval', 'closed_lost'],
  internal_approval: ['costing', 'regulatory_review', 'ready_to_send', 'closed_lost'],
  ready_to_send: ['internal_approval', 'sent', 'expired'],
  sent: ['changes_requested', 'accepted', 'expired'],
  changes_requested: ['brief_in_progress', 'costing', 'regulatory_review', 'closed_lost'],
  accepted: ['deposit_pending', 'handoff_ready'],
  deposit_pending: ['handoff_ready', 'closed_lost'],
  handoff_ready: ['handed_off'],
  handed_off: ['closed_won'],
  closed_won: [],
  closed_lost: [],
  expired: ['brief_in_progress', 'closed_lost'],
};

const quotationTransitions: Readonly<Record<QuotationState, readonly QuotationState[]>> = {
  draft: ['costing_review', 'withdrawn'],
  costing_review: ['draft', 'regulatory_review', 'management_approval', 'withdrawn'],
  regulatory_review: ['draft', 'management_approval', 'withdrawn'],
  management_approval: ['draft', 'approved_to_send', 'withdrawn'],
  approved_to_send: ['sent', 'withdrawn'],
  sent: ['changes_requested', 'accepted', 'expired', 'withdrawn'],
  changes_requested: ['superseded'],
  accepted: [],
  expired: ['superseded'],
  withdrawn: [],
  superseded: [],
};

export function canTransitionOpportunity(from: OpportunityStage, to: OpportunityStage): boolean {
  return opportunityTransitions[from].includes(to);
}

export function canTransitionQuotation(from: QuotationState, to: QuotationState): boolean {
  return quotationTransitions[from].includes(to);
}

export function isImmutableQuotationState(state: QuotationState): boolean {
  return ['sent', 'accepted', 'expired', 'withdrawn', 'superseded'].includes(state);
}

export const applicationErrorCodes = [
  'validation_error',
  'authentication_required',
  'permission_denied',
  'not_found',
  'version_conflict',
  'invalid_transition',
  'immutable_record',
  'missing_dependency',
  'regulatory_unknown',
  'regulatory_stale',
  'costing_incomplete',
  'offline_restricted',
  'rate_limited',
  'external_service_failure',
] as const;

export const applicationErrorCodeSchema = z.enum(applicationErrorCodes);
export type ApplicationErrorCode = z.infer<typeof applicationErrorCodeSchema>;

export class ApplicationError extends Error {
  readonly code: ApplicationErrorCode;
  readonly requestId?: string;

  constructor(code: ApplicationErrorCode, message: string, requestId?: string) {
    super(message);
    this.name = 'ApplicationError';
    this.code = code;
    if (requestId !== undefined) this.requestId = requestId;
  }
}

function canonicalize(value: unknown): string {
  if (value === null || typeof value === 'boolean' || typeof value === 'number') {
    return JSON.stringify(value);
  }
  if (typeof value === 'string') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonicalize).join(',')}]`;
  if (typeof value === 'object') {
    const entries = Object.entries(value as Readonly<Record<string, unknown>>)
      .filter(([, item]) => item !== undefined)
      .sort(([left], [right]) => left.localeCompare(right));
    return `{${entries
      .map(([key, item]) => `${JSON.stringify(key)}:${canonicalize(item)}`)
      .join(',')}}`;
  }
  throw new ApplicationError('validation_error', `Unsupported canonical value: ${typeof value}`);
}

export async function canonicalSha256(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalize(value));
  const digest = await globalThis.crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

export * from './oem';
